import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import {
  useTranslator,
  useAppContext,
  mealPlaceholderImage,
  formatIsoDate,
  FormikInput as Input,
  FormikCheckboxInput as Checkbox,
  formatIsoDateTime,
  useToast,
  callNativeMobileApp,
  formatCalories,
  withMobileAppMode,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import MacroDonutChart from '../../Layout/Chart/MacroDonutChart';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import validationSchema from '../AddMeal/validationSchema';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { TrackerAddMealProps, Ingredient } from '../types';
import { useGlobalIngredients } from '../hooks/useGlobalIngredients';
import { useGlobalIngredientsEdit } from '../hooks/useGlobalIngredientsEdit';

const STORAGE_KEY_SHOULD_USE_DATA = 'tracker_should_use_data_from_session_storage';

const TrackerAddMeal: React.FC<TrackerAddMealProps> = ({
  mealData,
  addMeal,
  addUserMeal,
  saveAsUserMeal,
  mode,
  mobileAppMode,
}) => {
  const { t, language } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  const { date } = useParams();
  const [showDateInput, setShowDateInput] = useState(
    date && date !== formatIsoDate(new Date()) ? true : false
  );

  const {
    consts: { APP_BRAND },
    images: { editPrimaryIcon },
  } = useAppContext();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDateObj = new Date(today);
  minDateObj.setDate(minDateObj.getDate() - 30);
  const minDate = formatIsoDate(minDateObj);

  const maxDateObj = new Date(today);
  maxDateObj.setDate(maxDateObj.getDate() + 30);
  const maxDate = formatIsoDate(maxDateObj);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const { ingredients: localIngredients, updateIngredients } = useGlobalIngredients(
    mealData.ingredients
  );

  const { updateIngredients: updateIngredientsEdit } = useGlobalIngredientsEdit(
    mealData.ingredients
  );

  // Get meal name from sessionStorage if flag is set, otherwise use original
  const shouldUseData = sessionStorage.getItem(STORAGE_KEY_SHOULD_USE_DATA) === 'true';
  const mealName = shouldUseData
    ? sessionStorage.getItem('tracker_meal_name') || mealData.name
    : mealData.name;

  // After reset, save original ingredients to sessionStorage
  useEffect(() => {
    const shouldUseData = sessionStorage.getItem(STORAGE_KEY_SHOULD_USE_DATA) === 'true';

    if (!shouldUseData) {
      console.log('shouldResetData - saving ingredients to sessionStorage');
      updateIngredients(mealData.ingredients);
      updateIngredientsEdit(mealData.ingredients);
    }

    if (shouldUseData) {
      // reset tracker_ingredients_edit
      updateIngredientsEdit(localIngredients);
    }
  }, [mealData.ingredients, updateIngredients, updateIngredientsEdit]);

  // Clear sessionStorage on navigation back or page unload when in backward-navigation mode
  useEffect(() => {
    const clearSessionStorage = () => {
      if (mode === 'backward-navigation') {
        sessionStorage.removeItem('tracker_ingredients');
        sessionStorage.removeItem('tracker_ingredients_edit');
        sessionStorage.removeItem('tracker_multiplier');
        sessionStorage.removeItem('tracker_meal_name');
        sessionStorage.removeItem('tracker_should_use_data_from_session_storage');
        sessionStorage.removeItem('tracker_extracted_meal');
        sessionStorage.removeItem('tracker_extracted_meal_name');
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      clearSessionStorage();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      clearSessionStorage();
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup function - also clears storage when component unmounts (e.g., React Router navigation)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      clearSessionStorage();
    };
  }, [mode]);

  // Function to calculate total macros from ingredients
  const calculateMacrosFromIngredients = (ingredients: Ingredient[]) => {
    const totalMacro = {
      protein: { grams: 0, percentage: 0 },
      fat: { grams: 0, percentage: 0 },
      carbohydrates: { grams: 0, percentage: 0 },
    };

    let totalKcal = 0;

    ingredients.forEach((ingredient) => {
      totalKcal += ingredient.kcal;

      if (ingredient.macro) {
        totalMacro.protein.grams += ingredient.macro.protein.grams;
        totalMacro.fat.grams += ingredient.macro.fat.grams;
        totalMacro.carbohydrates.grams += ingredient.macro.carbohydrates.grams;
      }
    });

    // Calculate percentages based on total kcal
    if (totalKcal > 0) {
      // Protein: 4 kcal per gram
      totalMacro.protein.percentage = Math.round(
        ((totalMacro.protein.grams * 4) / totalKcal) * 100
      );
      // Fat: 9 kcal per gram
      totalMacro.fat.percentage = Math.round(((totalMacro.fat.grams * 9) / totalKcal) * 100);
      // Carbohydrates: 4 kcal per gram
      totalMacro.carbohydrates.percentage = Math.round(
        ((totalMacro.carbohydrates.grams * 4) / totalKcal) * 100
      );
    }

    return {
      kcal: totalKcal,
      macro: totalMacro,
    };
  };

  // Calculate macros from localIngredients
  const calculatedMealData = useMemo(() => {
    return calculateMacrosFromIngredients(localIngredients);
  }, [localIngredients]);

  const handleDateInputBlur = (value: string | number) => {
    // If date is still today's date, hide the input and show "Today" div
    if (value === formatIsoDate(new Date())) {
      setShowDateInput(false);
    } else {
      setShowDateInput(true);
    }
  };

  const renderMealImage = () => {
    let mealImg;
    if (mealData.preparationImageUrl) {
      mealImg = mealData.preparationImageUrl;
    } else if (mealData.mealType !== undefined) {
      mealImg = mealPlaceholderImage(mealData.mealType, APP_BRAND as string);
    } else {
      // Fallback for cookbook meals or when mealType is not available
      mealImg = mealData.preparationImageUrl;
    }

    return <img src={mealImg} className="img-fluid rounded mb-3 mb-md-0" alt="" />;
  };

  const initialValues = {
    date: date ? date : formatIsoDate(new Date()),
    time: getCurrentTime(),
    servings: mealData.portions,
    saveYourMeal: false,
  };

  const handleGoToTracker = (date: string) => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_modal',
        payload: {
          path: `/tracker/${date}`,
        },
      });
    } else {
      if (mode === 'backward-navigation') {
        navigate(`/tracker/${date}`);
      }
    }
  };

  const handleSubmit = async (values: {
    date: string;
    time: string;
    servings: number;
    saveYourMeal: boolean;
  }) => {
    setIsSubmitting(true);

    // Map unit names to API format
    const mapUnitToApiFormat = (unit: string): string => {
      const unitMap: { [key: string]: string } = {
        g: 'grams',
        ml: 'milliliters',
        'fl oz': 'fl_oz',
        cl: 'cl',
      };
      return unitMap[unit] || unit;
    };

    const requestIngredients = localIngredients.map((ingredient: Ingredient) => {
      const unit =
        ingredient.measurements.length > 1
          ? ingredient.measurements[1].unit
          : ingredient.measurements[0].unit;

      const requestIngredient: {
        name: string;
        brand?: string;
        baseAmount: number;
        baseUnit: string;
        calories: number;
        proteinGrams: number | undefined;
        fatGrams: number | undefined;
        carbsGrams: number | undefined;
        measureId?: number;
        measureAmount?: number;
      } = {
        name: ingredient.name,
        brand: ingredient.brand,
        baseAmount:
          ingredient.measurements.length > 1
            ? ingredient.measurements[1].amount
            : ingredient.measurements[0].amount,
        baseUnit: mapUnitToApiFormat(unit),
        calories: Math.round(ingredient.kcal),
        proteinGrams: ingredient.macro?.protein.grams,
        fatGrams: ingredient.macro?.fat.grams,
        carbsGrams: ingredient.macro?.carbohydrates.grams,
      };

      if (ingredient.measurements.length > 1) {
        requestIngredient.measureId = ingredient.measurements[0].measureId;
        requestIngredient.measureAmount = ingredient.measurements[0].amount;
      }

      return requestIngredient;
    });

    const request: {
      dateTime: string;
      mealName: string;
      portions: number;
      ingredients: typeof requestIngredients;
      sourceType: string;
      mealId?: number;
      imageUrl?: string;
    } = {
      dateTime: formatIsoDateTime(values.date, `${values.time}:00`),
      mealName: mealName,
      portions: +values.servings,
      ingredients: requestIngredients,
      sourceType: mealData.sourceType,
      imageUrl: mealData.preparationImageUrl,
    };

    const requestUserMeal = {
      dateTime: formatIsoDateTime(values.date, `${values.time}:00`),
      baseMealId: mealData.id,
      portions: +values.servings,
    };

    if (mode === 'backward-navigation') {
      request.mealId = mealData.id;
    }

    if (!addUserMeal || shouldUseData) {
      try {
        const addMealResponse = await addMeal?.(request, values.saveYourMeal);

        if (addMealResponse?.__typename === 'DailyTrackerAddMealSuccess') {
          const mealId = (addMealResponse as { meal?: { id?: number } })?.meal?.id;

          // save as user meal if checkbox is checked
          if (values.saveYourMeal && mealId) {
            console.log('Saving as user meal');
            try {
              console.log('Saving as user meal', {
                loggedMealId: mealId,
              });
              const saveAsUserMealResponse = await saveAsUserMeal?.({
                loggedMealId: mealId,
              });

              console.log('Save as user meal response', saveAsUserMealResponse);

              if (saveAsUserMealResponse?.__typename === 'DailyTrackerSaveAsUserMealSuccess') {
                console.log('User meal saved successfully');
              } else {
                console.error('Failed to save as user meal:', saveAsUserMealResponse);
              }
            } catch (error) {
              console.error('Error saving as user meal:', error);
            }
          }

          // Clear sessionStorage
          sessionStorage.removeItem('tracker_ingredients');
          sessionStorage.removeItem('tracker_ingredients_edit');
          sessionStorage.removeItem('tracker_multiplier');
          sessionStorage.removeItem('tracker_meal_name');
          sessionStorage.removeItem('tracker_should_use_data_from_session_storage');
          sessionStorage.removeItem('tracker_extracted_meal');
          sessionStorage.removeItem('tracker_extracted_meal_name');

          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: values.date,
            },
          });

          if (mode === 'backward-navigation') {
            navigate(-1);
            toastSuccess(
              <p className="mb-0 w-100">
                {t('meals.notification.trackerConfirmation.content')}

                <Button color="link" onClick={() => handleGoToTracker(values.date)}>
                  {t('meals.notification.trackerConfirmation.button')}
                </Button>
              </p>
            );
          } else {
            setTimeout(() => {
              callNativeMobileApp({
                action: 'show_toast',
                payload: {
                  type: 'success',
                  content: `${t('meals.notification.trackerConfirmation.content')}`,
                  // buttonText: `${t('meals.notification.trackerConfirmation.button')}`,
                  // buttonUrl: `/tracker/${values.date}`,
                },
              });
              callNativeMobileApp({
                action: 'close_modal',
              });
              navigate(-Number(sessionStorage.getItem('tracker_depth')));
            }, 0);

            if (!mobileAppMode) {
              toastSuccess(
                <p className="mb-0 w-100">{t('meals.notification.trackerConfirmation.content')}</p>
              );
            }
          }
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // add user meal
      try {
        const addUserMealResponse = await addUserMeal?.(requestUserMeal);

        if (addUserMealResponse?.__typename === 'DailyTrackerAddUserMealSuccess') {
          // Clear sessionStorage
          sessionStorage.removeItem('tracker_ingredients');
          sessionStorage.removeItem('tracker_ingredients_edit');
          sessionStorage.removeItem('tracker_multiplier');
          sessionStorage.removeItem('tracker_meal_name');
          sessionStorage.removeItem('tracker_should_use_data_from_session_storage');
          sessionStorage.removeItem('tracker_extracted_meal');
          sessionStorage.removeItem('tracker_extracted_meal_name');

          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: values.date,
            },
          });

          navigate(-1);
          toastSuccess(
            <p className="mb-0 w-100">
              {t('meals.notification.trackerConfirmation.content')}

              <Button color="link" onClick={() => handleGoToTracker(values.date)}>
                {t('meals.notification.trackerConfirmation.button')}
              </Button>
            </p>
          );
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const fakeSubmit = () => {
    // blur input
    inputRef.current?.blur();
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={fakeSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ isValid, values }) => {
          // Helper function to format macro values
          const formatMacroDisplay = (originalGrams: number, originalPercentage: number) => {
            const multipliedGrams = Math.round(originalGrams * values.servings);

            // If multiplied grams is 0 but original percentage > 0, show "<1g"
            if (multipliedGrams === 0 && originalPercentage > 0) {
              return '<1';
            }

            return multipliedGrams;
          };

          const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
          // save servings to sessionStorage
          sessionStorage.setItem(
            MULTIPLIER_STORAGE_KEY,
            Math.max(values.servings, 0.001).toString()
          );

          // Calculate multiplied values based on servings
          const multipliedKcal = Math.round(calculatedMealData.kcal * values.servings);
          const multipliedMacro = calculatedMealData.macro
            ? {
                protein: {
                  grams: formatMacroDisplay(
                    calculatedMealData.macro.protein.grams,
                    calculatedMealData.macro.protein.percentage
                  ),
                },
                fat: {
                  grams: formatMacroDisplay(
                    calculatedMealData.macro.fat.grams,
                    calculatedMealData.macro.fat.percentage
                  ),
                },
                carbohydrates: {
                  grams: formatMacroDisplay(
                    calculatedMealData.macro.carbohydrates.grams,
                    calculatedMealData.macro.carbohydrates.percentage
                  ),
                },
              }
            : {
                protein: { grams: 0 },
                fat: { grams: 0 },
                carbohydrates: { grams: 0 },
              };

          return (
            <Form>
              <div className="page-template-tracker">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <Row>
                            <Col md="6">{renderMealImage()}</Col>
                            <Col md="6">
                              <div className="d-flex">
                                <div className="me-1 position-relative">
                                  <Input
                                    name="date"
                                    type="date"
                                    min={minDate}
                                    max={maxDate}
                                    translateErrMsg
                                    callbackOnChange={handleDateInputBlur}
                                  />
                                  <div className={!showDateInput ? 'today' : 'd-none'}>
                                    {t('time.day.today')}
                                  </div>
                                </div>
                                <div className="me-1">
                                  <Input name="time" type="time" translateErrMsg />
                                </div>
                              </div>

                              <h2 className="title-large semi-bold mt-xxs mb-l">{mealName}</h2>

                              <div className="mb-xl">
                                <Input
                                  horizontal
                                  label={t('tracker.servings.title')}
                                  name="servings"
                                  type="number"
                                  translateErrMsg
                                  min={0.1}
                                  max={30}
                                  step={0.01}
                                  className="text-center"
                                  innerRef={inputRef}
                                />
                              </div>

                              <Row className="align-items-center">
                                <Col xs="4">
                                  <div className="macro-chart-container">
                                    <MacroDonutChart
                                      proteinPercentage={
                                        calculatedMealData.macro?.protein.percentage || 0
                                      }
                                      fatPercentage={calculatedMealData.macro?.fat.percentage || 0}
                                      carbsPercentage={
                                        calculatedMealData.macro?.carbohydrates.percentage || 0
                                      }
                                      missingPercentage={calculatedMealData.macro ? 0 : 100}
                                      chartId="tracker-meal"
                                    />
                                    <div className="text">
                                      <p className="title-small semi-bold mb-0">
                                        {formatCalories(multipliedKcal)}
                                      </p>
                                      <p className="description-small mb-0">kcal</p>
                                    </div>
                                  </div>
                                </Col>
                                <Col xs="8">
                                  {/* macrro */}
                                  <Row className="" noGutters>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet protein"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.proteins')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.protein.grams} g
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet fat"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.fats')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.fat.grams} g
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col>
                                      <div className="d-flex align-items-start">
                                        <div className="bullet carbs"></div>
                                        <div>
                                          <p className="description-small mb-0">
                                            {t('tracker.macros.carbs')}
                                          </p>
                                          <p className="semi-bold mb-0">
                                            {multipliedMacro.carbohydrates.grams} g
                                          </p>
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>
                      </div>

                      <div className="tracker-ingredients-list">
                        <p className="text-text-main-color semi-bold mb-m d-flex justify-content-between">
                          {t('tracker.logMeal.ingredients.title')}

                          {mode === 'backward-navigation' ? (
                            <Link
                              to={`edit-meal`}
                              className="small"
                              state={{
                                ingredients: localIngredients,
                                mealName: mealName,
                                timestamp: Date.now(), // Force re-render
                              }}
                            >
                              <img src={editPrimaryIcon} alt="edit" className="me-1" />
                              {t('tracker.logMeal.ingredients.editButton')}
                            </Link>
                          ) : (
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => navigate(-1)}
                            >
                              <img src={editPrimaryIcon} alt="edit" className="me-1" />
                              {t('tracker.logMeal.ingredients.editButton')}
                            </Button>
                          )}
                        </p>

                        <ul className="tracker-ingredients-list-items">
                          {localIngredients.map((ingredient: Ingredient, index: number) => (
                            <li
                              key={ingredient.id || `ingredient_${index}`}
                              className="tracker-ingredient-item"
                            >
                              <div className="d-flex justify-content-between">
                                <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                  {ingredient.name}
                                  {ingredient.brand && ` (${ingredient.brand})`}
                                </span>
                                <span className="small text-text-main-color semi-bold">
                                  {ingredient.measurements &&
                                    ingredient.measurements.length > 1 && (
                                      <MeasurementUnits
                                        measurements={[ingredient.measurements[1]]}
                                        multiplier={values.servings}
                                      />
                                    )}

                                  {ingredient.measurements &&
                                    (ingredient.measurements[0].unit === 'ml' ||
                                      ingredient.measurements[0].unit === 'milliliters' ||
                                      ingredient.measurements[0].unit === 'cl' ||
                                      ingredient.measurements[0].unit === 'oz' ||
                                      ingredient.measurements[0].unit === 'fl oz' ||
                                      ingredient.measurements[0].unit === 'fl_oz' ||
                                      ingredient.measurements[0].unit === 'g' ||
                                      ingredient.measurements[0].unit === 'grams') && (
                                      <MeasurementUnits
                                        measurements={[ingredient.measurements[0]]}
                                        multiplier={values.servings}
                                      />
                                    )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="description text-text-variant-2-color">
                                  {formatCalories(ingredient.kcal * values.servings)} kcal
                                  <span className="d-inline-block ms-3">
                                    {t('summary.macros.proteinsShort')}{' '}
                                    {(
                                      Math.round(
                                        (ingredient.macro?.protein?.grams ?? 0) *
                                          values.servings *
                                          10
                                      ) / 10
                                    )
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.fatsShort')}{' '}
                                    {(
                                      Math.round(
                                        (ingredient.macro?.fat?.grams ?? 0) * values.servings * 10
                                      ) / 10
                                    )
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.carbsShort')}{' '}
                                    {(
                                      Math.round(
                                        (ingredient.macro?.carbohydrates?.grams ?? 0) *
                                          values.servings *
                                          10
                                      ) / 10
                                    )
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                </span>
                                <span className="description text-text-variant-2-color">
                                  {ingredient.measurements &&
                                    ingredient.measurements[0].unit !== 'ml' &&
                                    ingredient.measurements[0].unit !== 'milliliters' &&
                                    ingredient.measurements[0].unit !== 'cl' &&
                                    ingredient.measurements[0].unit !== 'oz' &&
                                    ingredient.measurements[0].unit !== 'fl oz' &&
                                    ingredient.measurements[0].unit !== 'fl_oz' &&
                                    ingredient.measurements[0].unit !== 'g' &&
                                    ingredient.measurements[0].unit !== 'grams' && (
                                      <>
                                        <span className="me-1">≈</span>
                                        <MeasurementUnits
                                          measurements={[ingredient.measurements[0]]}
                                          multiplier={values.servings}
                                        />
                                      </>
                                    )}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        {(!addUserMeal || shouldUseData) && (
                          <div className="save-your-meal-container">
                            <Checkbox name="saveYourMeal">
                              <p className="mb-0 small text-text-variant-color">
                                {t('tracker.logToTracker.saveYourMeal.checkbox')}
                              </p>
                            </Checkbox>
                          </div>
                        )}
                        <Button
                          type="button"
                          onClick={() => handleSubmit(values)}
                          color="primary"
                          className="w-100"
                          disabled={isSubmitting || !isValid}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner />
                              <span className="ms-2">{t('data-processing')}</span>
                            </>
                          ) : (
                            t('tracker.meal.logButton')
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default withMobileAppMode(TrackerAddMeal);
